import React, { useRef, useState } from 'react';
import Card from '../../../../../muiComponents/Card';
import useTranslate from '../../../../../customHooks/useTranslate';
import ImgPlaceholderTransparentBackground from '../../../../../images/ImgPlaceholderTransparentBackground';
import Icon from '../../../../../muiComponents/Icon';
import DeleteImagePopUp from './DeleteImagePopUp';
import ImageCropPopUp from './logo/ImageCropPopUp';
import ImageUploadActions from './ImageUploadActions';
import { getImageDimensions } from '../../../utils/imageUtils';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import { useZitoneoSettings } from '../../../contexts/ZitoneoContentContext';
import { EMPTY_FILE_STATE } from '../../../utils/imageUtils';
import {
    MIN_LOGO_HEIGHT,
    MIN_LOGO_WIDTH,
} from '../../../utils/zitoneoServicesUtils';
import ZitoneoImage from '../../../../components/Image';

const LogoSection = () => {
    const translate = useTranslate();
    const { setErrorMessage } = useNotification();
    const { uploadLogoImageToDDBB, LogoFile, saveHotelContent } =
        useZitoneoSettings();
    const [isPopUpOpen, setIsPopUpOpen] = useState(false);
    const [isDeletePopUpOpen, setIsDeletePopUpOpen] = useState(false);
    const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);

    const handleClosePopUp = () => {
        setIsPopUpOpen(false);
        setSelectedLogoFile(null);
    };

    const handleSaveLogo = async (file: File) => {
        await uploadLogoImageToDDBB(file);
        handleClosePopUp();
    };

    const handleLogoSelection = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (!file) {
            event.target.value = '';
            return;
        }

        try {
            const { width, height } = await getImageDimensions(file);

            if (width < MIN_LOGO_WIDTH || height < MIN_LOGO_HEIGHT) {
                setErrorMessage(
                    `${translate(
                        'error_min_dimensions'
                    )} ${MIN_LOGO_WIDTH}x${MIN_LOGO_HEIGHT}px (${width}x${height}px)`
                );
                event.target.value = '';
                return;
            }
        } catch (error) {
            setErrorMessage(translate('error_invalid_image'));
            event.target.value = '';
            return;
        }

        setSelectedLogoFile(file);
        setIsPopUpOpen(true);
        event.target.value = '';
    };

    const handleRemoveLogo = () => {
        setIsDeletePopUpOpen(true);
    };

    const handleConfirmDeleteLogo = async () => {
        await saveHotelContent({
            IdLogoFile: 0,
            LogoFile: EMPTY_FILE_STATE,
        });
        setIsDeletePopUpOpen(false);
    };

    return (
        <div className='hotel-content-settings__section'>
            <div className='settings-section__header'>
                <h5 className='settings-section__title'>{translate('logo')}</h5>
                <p className='settings-section__description'>
                    {translate('logo_description')}
                </p>
            </div>
            <Card className='padding20 favicon-card flex-column gap-20'>
                <div className='flex gap-20'>
                    {LogoFile?.VirtualPath ? (
                        <div className='zitoneo-logo__container'>
                            {' '}
                            <ZitoneoImage
                                src={LogoFile.VirtualPath}
                                alt={translate('logo')}
                                className='zitoneo-logo'
                                useLocalBaseUrl={true}
                            />
                        </div>
                    ) : (
                        <div className='zitoneo-logo-placeholder'>
                            <ImgPlaceholderTransparentBackground />
                        </div>
                    )}

                    <div className='flex-column gap-20'>
                        <div className='flex-column gap-5'>
                            <p className='settings__label'>
                                {translate(
                                    LogoFile?.VirtualPath
                                        ? 'logo_uploaded'
                                        : 'no_logo_uploaded'
                                )}
                            </p>
                            <p>{translate('logo_file_description')}</p>
                        </div>
                        <ImageUploadActions
                            inputId='logo-upload'
                            inputRef={logoInputRef}
                            onChange={handleLogoSelection}
                            onRemove={handleRemoveLogo}
                            uploadLabel={translate('upload_logo')}
                            showRemove={Boolean(LogoFile?.VirtualPath)}
                        />
                    </div>
                </div>
                <DeleteImagePopUp
                    open={isDeletePopUpOpen}
                    image={{
                        name: LogoFile?.Caption || translate('logo'),
                        label: translate('logo'),
                        src: LogoFile?.VirtualPath,
                        alt: translate('logo'),
                    }}
                    onDelete={handleConfirmDeleteLogo}
                    onCancel={() => setIsDeletePopUpOpen(false)}
                />
                <ul className='zitoneo-favicon__recomendation-list'>
                    <li className='zitoneo-favicon__recomendation-item'>
                        <Icon iconName='Image' />
                        <p>{translate('logo_recommendation_1')}</p>
                    </li>
                    <li className='zitoneo-favicon__recomendation-item'>
                        <Icon iconName='Contrast' />
                        <p>{translate('logo_recommendation_2')}</p>
                    </li>
                </ul>
            </Card>
            <ImageCropPopUp
                isOpen={isPopUpOpen}
                file={selectedLogoFile}
                onClose={handleClosePopUp}
                onSave={handleSaveLogo}
            />
        </div>
    );
};

export default LogoSection;
